import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { toggleSidebar } from '../../store/slices/uiSlice';
import { logout } from '../../store/slices/authSlice';
import { Menu, X, Bell, LogOut, Settings } from 'lucide-react';
import ThemeToggle from '../UI/ThemeToggle';
import NotificationDropdown from '../UI/NotificationDropdown';
import RoleSwitcher from '../UI/RoleSwitcher';
import { useNotifications } from '../../hooks/useNotifications';
import { logError } from '../../utils/errorLogger';
import { Role } from '../../hooks/useRoleSwitching';

const Header: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { sidebarOpen, theme } = useAppSelector((state) => state.ui);
  const { user } = useAppSelector((state) => state.auth);
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  
  // Get notification data
  const { unreadCount } = useNotifications({ autoFetch: true });
  
  const userButtonRef = useRef<HTMLButtonElement>(null);
  const notificationButtonRef = useRef<HTMLButtonElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);


  // Handle authentication state changes
  useEffect(() => {
    if (!isAuthenticated && user === null) {
      // User has been logged out, close any open menus
      setShowUserMenu(false);
      setShowNotifications(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Check if click is outside user menu (button + dropdown)
      if (userButtonRef.current && !userButtonRef.current.contains(target) && 
          userMenuRef.current && !userMenuRef.current.contains(target)) {
        setShowUserMenu(false);
      }
      
      // Check if click is outside notification menu
      if (notificationButtonRef.current && !notificationButtonRef.current.contains(target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const handleLogout = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      await dispatch(logout(false)).unwrap();
    } catch (error: any) {
      logError('Logout failed', error);
      // Continue with local logout even if API fails
    }
    
    // Always close menu and navigate, regardless of API result
    setShowUserMenu(false);
    navigate('/');
  };


  const handleProfile = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    navigate(`/${user?.role}/profile`);
    setShowUserMenu(false);
  };

  const handleUserMenuClick = () => {
    const position = calculateDropdownPosition(userButtonRef);
    setDropdownPosition(position);
    setShowUserMenu(!showUserMenu);
    setShowNotifications(false);
  };

  const handleNotificationClick = () => {
    const position = calculateDropdownPosition(notificationButtonRef);
    setDropdownPosition(position);
    setShowNotifications(!showNotifications);
    setShowUserMenu(false);
  };

  const calculateDropdownPosition = (ref: React.RefObject<HTMLButtonElement | null>) => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      return {
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      };
    }
    return { top: 0, right: 0 };
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'host':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'guest':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'usher':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 w-full shadow-xl border-b z-[100] transition-all duration-300 backdrop-blur-sm ${
      theme === 'dark' 
        ? 'bg-gray-900/95 border-gray-700/50' 
        : 'bg-white/95 border-gray-200/50'
    }`}>
      {/* Content */}
      <div className="relative z-10">
        <div className="px-3 sm:px-4 lg:px-6 xl:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16 relative">
            {/* Left side */}
            <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
              {/* Sidebar toggle */}
              <button
                data-menu-button
                onClick={() => dispatch(toggleSidebar())}
                className={`p-1.5 sm:p-2 rounded-lg transition-all duration-300 lg:hidden ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:text-white hover:bg-gray-800'
                    : 'text-gray-600 hover:text-black hover:bg-gray-100'
                }`}
              >
                {sidebarOpen ? (
                  <X className="h-5 w-5 sm:h-6 sm:w-6" />
                ) : (
                  <Menu className="h-5 w-5 sm:h-6 sm:w-6" />
                )}
              </button>

              {/* Logo */}
              <button
                onClick={() => navigate('/')}
                className="relative group cursor-pointer"
                aria-label="Go to home page"
              >
                <img 
                  src="/logo.jpg" 
                  alt="Mirsal Logo" 
                  className={`h-8 w-8 sm:h-9 sm:w-9 lg:h-11 lg:w-11 rounded-lg sm:rounded-xl object-cover shadow-lg border-2 transition-all duration-300 group-hover:scale-105 ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                  }`}
                />
              </button>
            </div>

            {/* Center - Mirsal Logo - Absolutely positioned */}
            <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 hidden sm:block">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-mirsal-green rounded-md"
                aria-label="Go to home page"
              >
                <img 
                  src={theme === 'dark' ? "/mirsal_word.png" : "/mirsal_word_black.png"} 
                  alt="Mirsal" 
                  className="h-8 sm:h-10 lg:h-12 w-auto object-contain transition-all duration-300 hover:scale-105"
                />
              </button>
            </div>

            {/* Right side */}
            <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
              {/* Role Switcher - Show if user has multiple roles */}
              {user && user.roles && user.roles.length > 1 && (
                <div className="hidden lg:block">
                  <RoleSwitcher currentRole={(user.active_role || user.role || 'guest') as Role} />
                </div>
              )}

              {/* Theme Toggle - Hidden on mobile/tablet, shown on lg+ (when sidebar is hidden) */}
              <div className="hidden lg:block">
                <ThemeToggle />
              </div>

              {/* Notifications - Hidden on mobile/tablet, shown on lg+ (when sidebar is hidden) - Hidden for guests */}
              {(user?.role !== 'guest' && user?.active_role !== 'guest') && (
                <div className="relative hidden lg:block">
                  <button 
                    ref={notificationButtonRef}
                    onClick={handleNotificationClick}
                    className={`p-2.5 rounded-lg transition-all duration-300 relative hover:scale-105 ${
                      theme === 'dark'
                        ? 'text-gray-300 hover:text-white hover:bg-gray-800/80'
                        : 'text-gray-600 hover:text-black hover:bg-gray-100/80'
                    }`}
                    title="Notifications"
                  >
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                      <span className={`absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center border-2 ${
                        theme === 'dark' ? 'border-gray-900' : 'border-white'
                      }`}>
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {/* Notifications dropdown */}
                  <NotificationDropdown
                    isOpen={showNotifications}
                    onClose={() => setShowNotifications(false)}
                    position={dropdownPosition}
                    theme={theme}
                    triggerRef={notificationButtonRef}
                  />
                </div>
              )}

              {/* User menu */}
              <div className="relative">
                <button
                  ref={userButtonRef}
                  onClick={handleUserMenuClick}
                  className={`p-1.5 sm:p-2.5 rounded-lg sm:rounded-xl transition-all duration-300 hover:scale-105 ${
                    theme === 'dark'
                      ? 'hover:bg-gray-800/80'
                      : 'hover:bg-gray-100/80'
                  }`}
                >
                  <div className="h-8 w-8 sm:h-9 sm:w-9 bg-gray-700 rounded-lg sm:rounded-xl flex items-center justify-center">
                    <span className="text-xs sm:text-sm font-bold text-white">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </span>
                  </div>
                </button>

                {/* User dropdown menu */}
                {showUserMenu && (
                  <div 
                    ref={userMenuRef}
                    className={`fixed w-56 sm:w-64 rounded-xl shadow-xl border py-2 z-[110] ${
                      theme === 'dark'
                        ? 'bg-gray-800 border-gray-700'
                        : 'bg-white border-gray-200'
                    }`}
                    style={{ right: `${dropdownPosition.right}px`, top: `${dropdownPosition.top}px` }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <div className={`px-4 py-3 border-b ${
                      theme === 'dark' ? 'border-gray-700' : 'border-gray-100'
                    }`}>
                      <p className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-black'}`}>
                        {user?.first_name} {user?.last_name}
                      </p>
                      <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{user?.email}</p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border mt-1 ${getRoleColor(user?.role || '')}`}>
                        {user?.role?.toUpperCase()}
                      </span>
                    </div>
                    
                    <button
                      onClick={handleProfile}
                      className={`flex items-center w-full px-4 py-3 text-sm transition-colors ${
                        theme === 'dark'
                          ? 'text-white hover:bg-gray-700'
                          : 'text-black hover:bg-gray-50'
                      }`}
                    >
                      <Settings className="h-4 w-4 mr-3" />
                      Profile Settings
                    </button>
                    
                    <button
                      onClick={handleLogout}
                      className={`flex items-center w-full px-4 py-3 text-sm transition-colors ${
                        theme === 'dark'
                          ? 'text-red-400 hover:bg-red-900/20'
                          : 'text-red-600 hover:bg-red-50'
                      }`}
                    >
                      <LogOut className="h-4 w-4 mr-3" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed top-14 sm:top-16 bottom-0 left-0 right-0 bg-gray-600 bg-opacity-75 z-30 lg:hidden"
          onClick={() => dispatch(toggleSidebar())}
        />
      )}

      {/* Click outside handlers - Temporarily disabled for debugging */}
      {/* {(showUserMenu || showNotifications) && (
        <div 
          className="fixed inset-0 z-[100]"
          onClick={(e) => {
            // Only close if clicking on the backdrop itself, not on child elements
            if (e.target === e.currentTarget) {
              // Click outside detected - closing menus
              setShowUserMenu(false);
              setShowNotifications(false);
            }
          }}
        />
      )} */}
    </header>
  );
};

export default Header;